import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserAssetsService } from 'src/app/pages/user-asset/user-assets.service';
import { BonusApi } from 'src/app/shared/apis/bonus.api';
import { CSSelectComponent } from 'src/app/shared/components/cs-select/cs-select.component';
import { CustomizeFormModule } from 'src/app/shared/components/customize-form/customize-form.module';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';
import { WalletService } from './../../wallet.service';
import { DepositBonusDropdownService } from './deposit-bonus-dropdown.service';

@Component({
  selector: 'app-deposit-bonus-dropdown',
  standalone: true,
  imports: [CustomizeFormModule, PipesModule, CSSelectComponent],
  templateUrl: './deposit-bonus-dropdown.component.html',
  styleUrl: './deposit-bonus-dropdown.component.scss',
})
export class DepositBonusDropdownComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private depositBonusDropdownService: DepositBonusDropdownService,
    private bonusApi: BonusApi,
    private walletService: WalletService,
    private userAssetsService: UserAssetsService,
    private route: ActivatedRoute,
  ) {}

  /** bonus variable */
  // 當前選擇的紅利 雙向綁定
  selectedBonus = '';
  // 紅利的最低存款金額與返回的倍率
  rateVos: { minDepositUsdt: number; rate: number; minDeposit: number } | null = null;
  // 這邊用any[]沒用到BonusList[]，是因為這邊要符合cs-select的資料格式
  bonusList: any[] = [];
  // 紅利加載狀態
  bonusLoading: boolean = false;
  // 是否第一次載入，因為ngOnChanges會在ngOnInit之前執行
  isFirst: boolean = true;

  // 所有匯率base USDT
  allRateBaseUsdt: { currency: string; rate: number }[] = [];

  @Input() currentCurrency: string = 'USDT';
  @Output() bonusLoadingChange = new EventEmitter<boolean>();
  @Output() activityNo = new EventEmitter<string>();

  bonusId: string = '';

  ngOnInit(): void {
    // 如果有暫存的bonus資料，就直接使用暫存資料
    // 否則就重新獲取bonus資料
    if (this.depositBonusDropdownService.preservedBonusState) {
      this.selectedBonus = this.depositBonusDropdownService.preservedBonusState.selectedBonus;
      this.bonusList = this.depositBonusDropdownService.preservedBonusState.bonusList;
      this.rateVos = this.bonusList.find(x => x.bonusActivitiesNo === this.selectedBonus)?.rateVos[0] || null;
    } else {
      (async () => {
        const params = await new Promise<any>(resolve => {
          this.route.queryParams.subscribe(resolve);
        });
        this.bonusId = params['bonusId'] || '';
        this.checkBonus(true);
      })();
    }
    this.isFirst = false;
    // 獲取匯率用於minDeposit顯示
    this.userAssetsService.allRate.subscribe(data => {
      this.allRateBaseUsdt = data.rates;
    });
  }
  ngOnChanges(): void {
    // 如果是第一次載入，ngOnInit已經處理過了，就不需要再檢查bonus
    if (this.isFirst) return;
    this.checkBonus(false);
  }

  ngOnDestroy(): void {
    // 只有到qrCode頁面要保留bonus資料，其餘都銷毀
    if (this.walletService.walletDialogType$.getValue() !== 'address_qr') {
      this.depositBonusDropdownService.clearBonusState();
    }
  }

  /**
   * 獲取存款紅利與整理資料
   *
   * @param currency
   * @param amount
   */
  checkBonus(isInit = false) {
    this.bonusLoading = true;
    this.bonusLoadingChange.emit(this.bonusLoading);

    // reset data start
    this.rateVos = null;
    this.selectedBonus = '';
    this.bonusList = [];
    this.depositBonusDropdownService.clearBonusState();
    // reset data end

    // 獲取deposit bonusList
    this.depositBonusDropdownService.getDepositBonus(this.currentCurrency).subscribe(data => {
      if (data) {
        this.bonusLoading = false;
        this.bonusLoadingChange.emit(this.bonusLoading);
        // 舊版裡面還有確認非黏性獎金/歐洲流程/paymentIQ的功能，後續要再添加
        this.bonusList = data.map(item => {
          return {
            ...item,
            label: this.depositBonusDropdownService.prizeAmountTypeTmp(item),
            value: item.bonusActivitiesNo,
            icon: 'fi-gift-filled',
          };
        });
        // 把不選擇紅利的資料push進去
        this.bonusList.push({ value: '', label: 'No Bonus', icon: '' });

        if (isInit && !this.bonusId) {
          this.noBonusSelected();
        } else {
          this.selectedBonus = this.bonusList.length > 1 ? this.bonusId || this.bonusList[0].bonusActivitiesNo : '';
          this.updateBonusSelection(this.selectedBonus);
        }
      }
    });
  }

  /**
   * 選擇紅利
   * 每次更改選擇的紅利都要存到service裡面
   *
   * @param value
   */
  changeBonus(value: string | null) {
    this.updateBonusSelection(value as string);
  }

  /**
   * 不選擇紅利
   */
  noBonusSelected() {
    this.selectedBonus = '';
    this.updateBonusSelection('');
  }

  /**
   * 每次選擇紅利都要打api，最後才能跟儲值匹配上
   * unknowtmpcode 為後端指定不選擇要傳的參數
   *
   * @param activityNo
   */
  sendBonusActivity(activityNo: string) {
    this.bonusApi
      .onCryptoActivityNo({
        activityNo: activityNo || 'unknowtmpcode',
      })
      .subscribe();
  }

  /**
   * 更新紅利選擇並處理相關邏輯
   * 將重複代碼提取到此方法
   *
   * @param bonusActivityNo 所選紅利活動編號
   */
  updateBonusSelection(bonusActivityNo: string): void {
    // 保存紅利信息到服務
    this.depositBonusDropdownService.saveBonusInfo(bonusActivityNo, this.bonusList);

    // 更新當前倍率信息
    this.rateVos = this.bonusList.find(x => x.bonusActivitiesNo === bonusActivityNo)?.rateVos[0] || null;
    // 如果有 rateVos，計算 minDeposit
    if (this.rateVos) {
      const rate = this.allRateBaseUsdt.find(r => r.currency === this.currentCurrency)?.rate || 1;
      this.rateVos.minDeposit = this.rateVos.minDepositUsdt.divide(rate);
    }

    // 發送紅利活動選擇到服務器
    this.sendBonusActivity(bonusActivityNo);

    // 發出選擇變更事件
    this.activityNo.emit(bonusActivityNo);
  }
}
